import React, { useEffect, useMemo, useRef } from 'react'
import { debounce } from 'lodash'
import { useField, useFormikContext } from 'formik'
import { TextField, TextFieldProps } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'

const DEBOUNCE_DELAY_MS = 300

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.grey[300],
      },
    },
  },
}))

type DebouncedTextFieldProps = {
  name: string
  label: string
  multiline?: boolean
  minRows?: number
  fullWidth?: boolean
  helperText?: string
  placeholder?: string
  disabled?: boolean
  type?: TextFieldProps['type']
  resizable?: boolean
}

/**
 * DebouncedTextField - An uncontrolled text field that syncs to Formik on blur or after debounce.
 *
 * Performance: During typing, this component does NOT trigger React re-renders or Formik state updates.
 * The browser handles keystrokes natively, resulting in near-zero CPU usage during typing.
 *
 * Sync behavior:
 * - On blur: Immediately syncs current value to Formik
 * - During typing: Syncs to Formik after x ms of no typing
 * - External changes: If Formik value changes externally (e.g., form reset), the input updates
 *
 * Use this for text inputs where performance during typing is critical.
 */
const DebouncedTextField: React.FC<DebouncedTextFieldProps> = ({
  name,
  label,
  multiline = false,
  minRows = 1,
  fullWidth = false,
  helperText,
  placeholder,
  disabled = false,
  type = 'text',
  resizable = false,
}) => {
  const [field, meta, helpers] = useField(name)
  const { status, submitCount } = useFormikContext()
  const viewOnly = Boolean((status as Record<string, unknown>)?.viewOnly)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const classes = useStyles()

  // Track the last synced value to detect external changes
  const lastSyncedValueRef = useRef<string>(field.value ?? '')

  const debouncedSync = useMemo(
    () =>
      debounce((value: string) => {
        helpers.setValue(value)
        lastSyncedValueRef.current = value
      }, DEBOUNCE_DELAY_MS),
    [helpers]
  )

  useEffect(
    () => () => {
      debouncedSync.cancel()
    },
    [debouncedSync]
  )

  // Handle external value changes (e.g., form reset, autofill)
  useEffect(() => {
    const formikValue = field.value ?? ''
    if (formikValue !== lastSyncedValueRef.current && inputRef.current) {
      // Formik value changed externally - update the input
      inputRef.current.value = formikValue
      lastSyncedValueRef.current = formikValue
    }
  }, [field.value])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSync(event.target.value)
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Cancel any pending debounced sync and sync immediately
    debouncedSync.cancel()
    const currentValue = event.target.value
    if (currentValue !== lastSyncedValueRef.current) {
      helpers.setValue(currentValue)
      lastSyncedValueRef.current = currentValue
    }
    helpers.setTouched(true)
  }

  return (
    <TextField
      inputRef={inputRef}
      name={name}
      label={label}
      defaultValue={field.value ?? ''}
      onChange={handleChange}
      onBlur={handleBlur}
      multiline={multiline}
      minRows={minRows}
      fullWidth={fullWidth}
      helperText={submitCount > 0 && meta.error ? meta.error : helperText}
      error={submitCount > 0 && Boolean(meta.error)}
      placeholder={placeholder}
      disabled={viewOnly || disabled}
      type={type}
      variant="outlined"
      size="small"
      className={classes.root}
      InputLabelProps={{
        // When value is set programmatically (e.g., autofill), MUI doesn't detect the input has
        // content and leaves the label un-shrunk, causing overlap. Force shrink when Formik has a value.
        shrink: field.value ? true : undefined,
      }}
      InputProps={{
        autoComplete: 'off',
      }}
      sx={resizable ? { '& textarea': { resize: 'vertical' } } : undefined}
    />
  )
}

export default DebouncedTextField
